import { useState } from "react";
import Button from "@mui/material/Button";
import type { PageContext } from "../stores/chat";
import type { FormContext } from "@/types";

function Values({ value }: { value: unknown }) {
  if (value === null || value === undefined) return <span>Not provided</span>;
  if (typeof value !== "object") return <span>{String(value)}</span>;
  return (
    <dl className="space-y-1 border-l border-border pl-2">
      {Object.entries(value)
        .filter(([, entry]) => entry !== undefined)
        .map(([key, entry]) => (
          <div key={key}>
            <dt className="font-medium">
              {Array.isArray(value)
                ? `Item ${Number(key) + 1}`
                : key.replace(/([a-z])([A-Z])/g, "$1 $2")}
            </dt>
            <dd className="break-words whitespace-pre-wrap">
              <Values value={entry} />
            </dd>
          </div>
        ))}
    </dl>
  );
}

export function PageContextPreview({
  context,
  formContext,
  getPageContext,
}: {
  context: PageContext;
  formContext?: FormContext | null;
  getPageContext?: () => PageContext;
}) {
  const [snapshot, setSnapshot] = useState<PageContext | null>(null);
  const shown = snapshot ?? context;
  const refresh = () => setSnapshot(getPageContext?.() ?? context);
  return (
    <details
      data-ai-context-ignore
      className="mt-2 text-xs"
      onToggle={(event) => {
        if (event.currentTarget.open) refresh();
      }}
    >
      <summary className="cursor-pointer font-medium">
        Preview page information
      </summary>
      <p className="my-2 text-muted-foreground">
        Current-page data, including content below the scroll position. No
        screenshot, unseen pages, or image interpretation. Sharing follows your
        choices above; data is refreshed again when you send.
      </p>
      <Button
        type="button"
        variant="text"
        size="small"
        className="mb-2"
        onClick={refresh}
      >
        Refresh preview
      </Button>
      {shown.screenContext?.truncated && (
        <p role="status" className="mb-2">
          Some details are omitted to keep this snapshot small.
        </p>
      )}
      <div
        className="max-h-72 overflow-auto rounded border border-border p-2"
        tabIndex={0}
        aria-label="Page context preview"
      >
        <Values
          value={{
            page: shown.page,
            filters: shown.filters,
            ...shown.screenContext,
          }}
        />
        {formContext && (
          <details className="mt-2">
            <summary>Facility wizard details available separately</summary>
            <Values value={formContext} />
          </details>
        )}
      </div>
    </details>
  );
}
